import dayjs from 'dayjs';
import uniq from 'lodash/uniq';
import { createSelector } from 'reselect';

import { RootState } from 'store/types';

import { formatDateToIso8601 } from 'modules/utils/DateUtils';
import { TimeSlot } from './types';
import {
  appointmentDateSelector,
  appointmentDetailsSelector,
  appointmentLocationSelector,
  appointmentTimeSelector
} from '../appointmentDetails/selectors';
import {
  npv2SelectedProviderLocationSlotsSelector,
  npv2TimeSlotsDataSelector
} from '../npv2/selectors';

export const timeSlotsSelector = (state: RootState) => state.booking.appointment.timeSlots;

export const timeSlotsFetchingSelector = createSelector(
  timeSlotsSelector,
  timeSlots => timeSlots.isFetching
);

export const timeSlotsErrorSelector = createSelector(
  timeSlotsSelector,
  timeSlots => timeSlots.error
);

export const timeSlotsDataSelector = createSelector(timeSlotsSelector, timeSlots => timeSlots.data);

export const loadedSlotsLocationIdsDataSelector = createSelector(
  timeSlotsSelector,
  timeSlots => timeSlots.loadedProviderLocations
);

export const datesWithTimeSlotsSelector = createSelector([timeSlotsDataSelector], timeSlots => {
  if (!timeSlots || !timeSlots.length) return [];

  return uniq(timeSlots?.map(ts => ts.datetime).map(dt => formatDateToIso8601(dt))).reduce(
    (hash, day) => ({ ...hash, [day]: true }),
    {}
  );
});

export const timeSlotsByAppointmentDateSelector = createSelector(
  [appointmentDateSelector, appointmentLocationSelector, timeSlotsDataSelector],
  (appointmentDate, appointmentLocation, timeSlots) => {
    if (!appointmentDate || !appointmentLocation || !timeSlots) {
      return [];
    }

    const res = new Map<string, TimeSlot[]>();

    const { locationId } = appointmentLocation;
    for (const timeSlot of timeSlots) {
      const isSameDay = dayjs(timeSlot.datetime).isSame(dayjs(appointmentDate), 'day');

      if (isSameDay && locationId) {
        const curr = res.get(locationId);

        if (curr) {
          res.set(locationId, [...curr, timeSlot]);
        } else {
          res.set(locationId, [timeSlot]);
        }
      }
    }
    return [...res.entries()].map(([location, slots]) => ({
      location,
      timeSlots: slots
    }));
  }
);

export const validateAppointmentTimeSelector = createSelector(
  [appointmentDetailsSelector, appointmentTimeSelector, npv2TimeSlotsDataSelector],
  (appointmentDetails, appointmentTime, npv2TimeDataObj) => {
    if (!appointmentDetails || !appointmentTime || !npv2TimeDataObj) {
      return false;
    }
    const { datetime } = appointmentTime;

    const appointmentType = appointmentDetails?.appointmentType;
    let synonymId = '';

    if (appointmentType) {
      const visitTypes = appointmentDetails?.groupedVisitType?.visitTypes || [];
      const visitType = visitTypes.find(type => type.appointmentType === appointmentType);

      if (visitType) {
        synonymId = visitType?.synonymId;
      }
    }

    const providerPersonalId = appointmentDetails?.doctor?.cernerPersonnelId.toString() || '';
    const locationId = appointmentDetails?.appointmentLocation?.locationId || '';
    const slotRequestLocationId = `${providerPersonalId}-${locationId}-${synonymId}`;
    const timeSlots = npv2TimeDataObj[slotRequestLocationId];
    return !!timeSlots?.find(slot => dayjs(slot.datetime).isSame(dayjs(datetime)));
  }
);

/**
 * This will check if the slots are loaded for a particular location for a provider.
 */
export const isLocationSlotsLoadedSelector = createSelector(
  [appointmentDetailsSelector, appointmentLocationSelector, npv2TimeSlotsDataSelector],
  (appointmentDetails, appointmentLocation, npv2TimeDataObj) => {
    if (!appointmentDetails || !appointmentLocation || !npv2TimeDataObj) {
      return false;
    }

    const appointmentType = appointmentDetails?.appointmentType;
    let synonymId = '';

    if (appointmentType) {
      const visitTypes = appointmentDetails?.groupedVisitType?.visitTypes || [];
      const visitType = visitTypes.find(type => type.appointmentType === appointmentType);

      if (visitType) {
        synonymId = visitType?.synonymId;
      }
    }

    const providerPersonalId = appointmentDetails?.doctor?.cernerPersonnelId.toString() || '';
    const locationId = appointmentDetails?.appointmentLocation?.locationId || '';
    const slotRequestLocationId = `${providerPersonalId}-${locationId}-${synonymId}`;
    const timeSlots = npv2TimeDataObj[slotRequestLocationId];
    return !!timeSlots?.length;
  }
);
